import { create } from "zustand";
import { persist } from "zustand/middleware";
import { AgentMessage } from "@/models/agent/message";
import { Thread } from "@/models/agent/thread";

interface AgentState {
  threads: Thread[];
  messages: AgentMessage[]; // 新しいメッセージが前
  latestStatusMessage: AgentMessage | null;
  latestTaskList: Array<{ content: string; status: string; }> | null;
  selectedToolCallMessage: AgentMessage | null;
  currentToolResponseMessage: AgentMessage | null;
  isOpenToolPanel: boolean;
  isToolPanelExpanded: boolean;
  optimisticlyProcessedCostIds: Set<string>;
  currentAgentRunId: string | null;
  addThreads: (threads: Thread[]) => void;
  resetThreads: () => void;
  pushOptimisticThread: (threadId: string, workspaceId: string, content: string) => void;
  addMessages: (newMessages: AgentMessage[]) => void;
  pushOptimisticMessage: (message: AgentMessage) => void;
  resetMessages: () => void;
  setLatestStatusMessage: (message: AgentMessage | null) => void;
  setLatestTaskList: (taskList: Array<{ content: string; status: string; }> | null) => void;
  setSelectedToolCallMessage: (message: AgentMessage | null) => void;
  setCurrentToolResponseMessage: (message: AgentMessage | null) => void;
  setIsOpenToolPanel: (isOpen: boolean) => void;
  setIsToolPanelExpanded: (isExpanded: boolean) => void;
  setCurrentAgentRunId: (agentRunId: string | null) => void;
}

export const useAgentStore = create<AgentState>()(
  persist(
    (set) => ({
      threads: [],
      messages: [],
      latestStatusMessage: null,
      latestTaskList: null,
      selectedToolCallMessage: null,
      currentToolResponseMessage: null,
      isOpenToolPanel: false,
      isToolPanelExpanded: false,
      optimisticlyProcessedCostIds: new Set<string>(),
      currentAgentRunId: null,
      addThreads: (threads) => set((state) => ({ threads: state.threads.concat(threads) })),
      resetThreads: () => set(() => ({ threads: [] })),
      pushOptimisticThread: (threadId, workspaceId, content) =>
        set((state) => {
          const newThread = new Thread(
            threadId,
            workspaceId,
            content,
            new Date(),
            new Date(),
          );
          return { threads: [newThread, ...state.threads] };
        }),
      addMessages: (newMessages) =>
        set((state) => {
          const existingIds = new Set(state.messages.map((msg) => msg.id));
          const filteredNewMessages = newMessages.filter((msg) => !existingIds.has(msg.id));

          // 初回ロード時のみwrite_tasksを探す（ページングでは探さない）
          const isInitialLoad = state.messages.length === 0;
          let newLatestTaskList = state.latestTaskList;

          if (isInitialLoad && filteredNewMessages.length > 0) {
            // 新規メッセージの中から最新のwrite_tasksを探す
            // messagesは新しいメッセージが前なので、先頭から探せば最新が見つかる
            const latestWriteTasksMessage = filteredNewMessages.find(
              (msg) => msg.writeTasksList !== null,
            );

            if (latestWriteTasksMessage) {
              newLatestTaskList = latestWriteTasksMessage.writeTasksList;
            }
          }

          return {
            messages: state.messages.concat(filteredNewMessages),
            latestTaskList: newLatestTaskList,
          };
        }),
      pushOptimisticMessage: (message) =>
        set((state) => {
          // write_tasks のtool_callを検出してタスクリストを更新
          const newTaskList = message.writeTasksList;

          if (message.metadata && !state.optimisticlyProcessedCostIds.has(message.metadata.event_id as string)) {
            return {
              messages: [message, ...state.messages],
              latestTaskList: newTaskList || state.latestTaskList,
              optimisticlyProcessedCostIds: message.metadata.event_id
                ? state.optimisticlyProcessedCostIds.add(message.metadata.event_id as string)
                : state.optimisticlyProcessedCostIds,
            };
          }

          return {
            messages: [message, ...state.messages],
            latestTaskList: newTaskList || state.latestTaskList,
          };
        }),
      resetMessages: () =>
        set(() => ({
          messages: [],
          latestTaskList: null,
        })),
      setLatestStatusMessage: (message) => set(() => ({ latestStatusMessage: message })),
      setLatestTaskList: (taskList) => set(() => ({ latestTaskList: taskList })),
      setSelectedToolCallMessage: (message) =>
        set((state) => {
          const toolId = message?.toolCall ? message.toolCall?.id : null;
          if (toolId) {
            const toolResponseMessage = state.messages.find(msg =>
              msg.role === "tool_response" && msg.toolResult?.id === toolId
            );
            state.setCurrentToolResponseMessage(toolResponseMessage || null);
          } else {
            state.setCurrentToolResponseMessage(null);
          }

          return { selectedToolCallMessage: message };
        }),
      setCurrentToolResponseMessage: (message) => set(() => ({ currentToolResponseMessage: message })),
      setIsOpenToolPanel: (isOpen) => set(() => ({ isOpenToolPanel: isOpen })),
      setIsToolPanelExpanded: (isExpanded) => set(() => ({ isToolPanelExpanded: isExpanded })),
      setCurrentAgentRunId: (agentRunId) => set(() => ({ currentAgentRunId: agentRunId })),
    }),
    {
      name: "agent-store",
      partialize: (state) => ({
        isToolPanelExpanded: state.isToolPanelExpanded,
      }),
    },
  ),
);
